import { useQuery } from '@apollo/client';
import {
  Box,
  Collapse,
  IconButton,
  LinearProgress,
  Stack,
  styled,
  Typography
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { INotification } from 'src/models/general';
import { CHECK_NOTIFICATION } from 'src/services/graphql/notification/query';
import CloseIcon from '@mui/icons-material/Close';
import CachedIcon from '@mui/icons-material/Cached';
import ButtonCircle from '../ButtonCircle';
import useRefreshObjectList from 'src/hooks/useRefreshObjectList';
// import AvTimerIcon from '@mui/icons-material/AvTimer';
// import { useCountDown } from 'src/hooks/useCountDown';
// import { formatMsToHourMinute } from 'src/utils/formatMsToHourMinute';
import { defaultPrimary } from 'src/theme/schemes/PureLightTheme';
import ProductTourTooltip from '../ProductTourTooltip';
import { FirstTimeTour } from 'src/constants/productTour/FirstTimeTour';
import { reducerBumpRefreshSearchToken } from 'src/features/advanceSearchLandingOverview';
import { useLocation } from 'react-router';

const CustomizedProgress = styled(LinearProgress)`
  background-color: #fff;
  & .MuiLinearProgress-barColorPrimary {
    background-color: #54cee9;
  }
`;

const EST_MINUTES_PER_ITEM = 50;

const PopulatingDataProgress = () => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const isLandingOverview = pathname.split('/')[1] === 'overview';
  const projectId = useAppSelector((state) => state.users.project.id);
  const isFromOnboarding = localStorage.getItem('isFromOnboarding');
  const [show, setShow] = useState<boolean>(!!isFromOnboarding);
  const [objectProgresses, setObjectProgresses] = useState<INotification[]>([]);
  const refreshObjectList = useRefreshObjectList();

  const {
    isOnTour,
    currentTour: { stepNumber, menu }
  } = useAppSelector((state) => state.storeProductTour);
  // const { timeLeftMs, isExpired, startCountdown, clearCountdown } =
  //   useCountDown();

  // Read from cache only - data populated by Activity component
  const { data } = useQuery(CHECK_NOTIFICATION, {
    fetchPolicy: 'cache-first',
    variables: { projectId },
    onCompleted: (response) => {
      if (!response) return;

      const OBJECT_ACTIVITY_TYPES = new Set([
        'firstTimeSocialMediaData',
        'copyKeyword'
      ]);

      const currentProgresses: INotification[] =
        response.notification_getNotification;

      const objectProgresses = currentProgresses.filter((activity) =>
        OBJECT_ACTIVITY_TYPES.has(activity.subTypeNotification)
      );
      setObjectProgresses(objectProgresses);
    },
    pollInterval: 10 * 1000,
    skip: !show
  });

  const successObjectProgresses = useMemo(() => {
    if (!objectProgresses.length) return [];
    return objectProgresses.filter((activity) => activity.status === 'success');
  }, [objectProgresses]);

  const progressValue = useMemo(() => {
    if (!successObjectProgresses.length) return 0;
    return Math.min(
      (successObjectProgresses.length / objectProgresses.length) * 100
    );
  }, [objectProgresses, successObjectProgresses]);

  const handleClickRefresh = () => {
    if (isLandingOverview) dispatch(reducerBumpRefreshSearchToken());
    refreshObjectList();
  };

  // const estimatedExpiredTime = useMemo(() => {
  //   if (!objectProgresses.length) return null;

  //   const pendingProgresses =
  //     objectProgresses.length - successObjectProgresses.length;

  //   if (pendingProgresses <= 0) return null;

  //   const ms = pendingProgresses * EST_MINUTES_PER_ITEM * 60 * 1000;

  //   return new Date(Date.now() + ms).toISOString();
  // }, [objectProgresses.length, successObjectProgresses.length]);

  // update count down according to extimated duration
  // useEffect(() => {
  //   if (!estimatedExpiredTime) {
  //     clearCountdown();
  //     return;
  //   }

  //   startCountdown(estimatedExpiredTime);
  // }, [estimatedExpiredTime, startCountdown, clearCountdown]);

  // only show if it's from onboarding page
  useEffect(() => {
    if (!isFromOnboarding) {
      setShow(false);
    }
    if (progressValue === 100) localStorage.removeItem('isFromOnboarding');
  }, []);

  const isCurrentlyOnTourFirstTimeStep2 =
    isOnTour && stepNumber === 2 && menu === FirstTimeTour.menu;

  return (
    <ProductTourTooltip
      open={isCurrentlyOnTourFirstTimeStep2}
      placement="bottom"
    >
      <Box
        sx={{
          width: '100%',
          bgcolor: defaultPrimary.main,
          ...(isCurrentlyOnTourFirstTimeStep2
            ? {
                position: 'relative',
                zIndex: 1000,
                pointerEvents: 'none'
              }
            : {})
        }}
      >
        <Collapse in={show}>
          <Stack
            sx={{ px: 3, py: 2 }}
            gap={1}
            direction="row"
            position="relative"
          >
            <Stack gap={1} flexGrow={1}>
              <Stack
                direction={'row'}
                alignItems={'center'}
                justifyContent={'space-between'}
              >
                <Typography variant="subtitle2" color="white">
                  Processing your data{' '}
                  <Typography
                    variant="subtitle2"
                    component="span"
                    color="white"
                    fontWeight={600}
                  >
                    {progressValue.toFixed()}%
                  </Typography>
                </Typography>
                {/* {!isExpired ? (
                <Stack direction={'row'} alignItems={'center'} gap={1}>
                  <AvTimerIcon fontSize="small" />
                  <Typography variant="subtitle2" color="white">
                    Est time remaining {formatMsToHourMinute(timeLeftMs)}
                  </Typography>
                </Stack>
              ) : null} */}
              </Stack>
              <CustomizedProgress
                value={progressValue}
                variant={data ? 'determinate' : 'indeterminate'}
              />
            </Stack>
            <Stack direction={'row'}>
              <ButtonCircle
                sx={{
                  background: '#E0F6FF'
                }}
                onClick={() => handleClickRefresh()}
              >
                <CachedIcon fontSize="inherit" sx={{ color: '#009fe3' }} />
              </ButtonCircle>
              <IconButton
                aria-label="close"
                onClick={() => {
                  setShow(false);
                }}
                sx={{
                  width: '16px',
                  height: '16px',
                  p: 1,
                  position: 'absolute',
                  right: 5,
                  top: 5,
                  color: (theme) => theme.palette.background.default
                }}
              >
                <CloseIcon sx={{ fontSize: '16px' }} />
              </IconButton>
            </Stack>
          </Stack>
        </Collapse>
      </Box>
    </ProductTourTooltip>
  );
};

export default PopulatingDataProgress;
